// /src/pages/cases/Create.tsx
import { East } from "@mui/icons-material";
import { CircularProgress, Stack, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FileImage from "../../assets/icons/file.svg";
import Button from "../../components/buttons/Button";
import { FileListWithProgress } from "../../components/file-management";
import FileUploadZone from "../../components/inputs/FileUpload/FileUploadZone";
import { FileUpload } from "../../components/inputs/types";
import BottomBar from "../../components/misc/BottomBar";
import { useAppContext } from "../../contexts/AppContext";
import { useError } from "../../contexts/ErrorContext";
import Translations from "../../lang/en/pages.json";
import Minimal from "../../layouts/Minimal";
import { useCaseManagement, useFileUpload } from "./hooks/useCaseManagement";
import {
  CaseFile,
  CreateCaseError,
  CreateRevisionError,
  FileUploadError,
} from "./types";
import { showUploadResult, removeFirstByName } from "./utils";
import { UNIFIED_UPLOAD_ENABLED } from "../../config";
import { useUpload } from "../../upload/useUpload";
import UploadResultsPanel from "../../upload/UploadResultsPanel";

const CreateV2: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const { isLoading } = useAppContext();
  const [selectedFiles, setSelectedFiles] = useState<(FileUpload | CaseFile)[]>([]);
  const controller = useUpload();

  const handleRemoveFile = (filename: string) => {
    const updated = removeFirstByName(selectedFiles, filename);
    setSelectedFiles(updated);
  };

  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) {
      showError(Translations.pages.new_case_files_page.messages.nofiles);
      return;
    }
    const files = selectedFiles
      .filter((f): f is FileUpload => "file" in f && f.file instanceof File)
      .map((f) => f.file as File);
    if (files.length === 0) {
      showError(Translations.pages.new_case_files_page.messages.nofiles);
      return;
    }
    try {
      await controller.start({ intent: "NEW_CASE", files });
    } catch {
      showError("Upload could not be started. Please try again.");
    }
  };

  // Navigate when session completes
  React.useEffect(() => {
    if (
      controller.session?.status === "COMPLETED" &&
      controller.session.case_id
    ) {
      navigate(`/cases/${controller.session.case_id}/status`);
    }
  }, [controller.session?.status, controller.session?.case_id, navigate]);

  const handleCancel = () => {
    controller.reset();
    navigate("/cases");
  };

  const canAnalyze =
    !isLoading &&
    !controller.isRunning &&
    selectedFiles.length > 0;

  return (
    <Minimal hidePages={true}>
      <Stack maxWidth={860} mb={4} alignItems="center" spacing={2} textAlign="center">
        <img src={FileImage} />
        <Typography color="success" fontWeight="bold" variant="h5">
          {Translations.pages.new_case_files_page.header_block.title}
        </Typography>
        <Typography variant="body1" fontWeight="light">
          {Translations.pages.new_case_files_page.header_block.content}
        </Typography>

        <FileUploadZone
          isLoading={isLoading || controller.isRunning}
          acceptZip
          panelMode="v2"
          value={selectedFiles}
          onChange={setSelectedFiles}
          buttonPosition="top"
          title={Translations.pages.files_page.upload_block.title}
          description={Translations.pages.files_page.upload_block.content}
          buttonText={Translations.pages.files_page.upload_block.button}
          renderFileList={() => (
            <UploadResultsPanel
              controller={controller}
              pendingFiles={selectedFiles}
              onRemoveFile={handleRemoveFile}
              onResubmit={handleAnalyze}
            />
          )}
        />
      </Stack>

      <BottomBar
        buttons={[
          <Button
            disabled={isLoading || controller.isRunning}
            sx={{ color: "black", fontWeight: "light" }}
            onClick={handleCancel}
            key={1}
            variant="text"
          >
            {Translations.pages.new_case_files_page.buttons.cancel}
          </Button>,
          <Button
            sx={{ px: 4 }}
            onClick={handleAnalyze}
            key={0}
            disabled={!canAnalyze}
            variant="green"
          >
            {controller.isRunning ? (
              <CircularProgress sx={{ color: "white" }} size={20} />
            ) : (
              <Stack direction="row" alignItems="center" gap={1}>
                <East />
                {Translations.pages.files_page.buttons.analyze}
              </Stack>
            )}
          </Button>,
        ]}
      />
    </Minimal>
  );
};

const CreateV1: React.FC = () => {
  const [updatedFiles, setUpdatedFiles] = useState<(FileUpload | CaseFile)[]>(
    []
  );
  const navigate = useNavigate();
  const { showSuccess, showError } = useError();
  const { createCase, createRevision } = useCaseManagement();
  const { uploadAll, setFiles } = useFileUpload();
  const { isLoading } = useAppContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileProgress, setFileProgress] = useState<Record<string, number>>({});

  const [currentCaseId, setCurrentCaseId] = useState<string | null>(null);
  const [currentRevisionId, setCurrentRevisionId] = useState<number | null>(
    null
  );

  const handleProgress = (file: File, progress: number) => {
    console.info(`File ${file.name} upload progress: ${progress}%`);
    setFileProgress((prev) => ({
      ...prev,
      [file.name]: progress,
    }));
  };

  const handleAnalyze = async () => {
    setIsSubmitting(true);

    try {
      if (updatedFiles.length === 0) {
        showError(Translations.pages.new_case_files_page.messages.nofiles);
        return;
      }

      const newCaseId = currentCaseId || (await createCase());
      const newRevisionId =
        currentRevisionId || (await createRevision(newCaseId));

      setCurrentCaseId(newCaseId);
      setCurrentRevisionId(newRevisionId);

      const result = await uploadAll(newCaseId, newRevisionId, updatedFiles, handleProgress);

      showUploadResult(result, Translations.pages.new_case_files_page.messages.success, showSuccess);
      navigate(`/cases/${newCaseId}/status`);
    } catch (error) {
      
      setCurrentCaseId(null);
      setCurrentRevisionId(null);
      if (error instanceof CreateCaseError) {
        showError(error.message);
      } else if (error instanceof CreateRevisionError) {
        showError(error.message);
      } else if (error instanceof FileUploadError) {
        const errorWithRetry = `${error.message}. Please remove invalid files and try again.`
        showError(errorWithRetry);
      } else {
        console.error("Unexpected error during case creation", error);
        const message =
          error instanceof Error ? error.message : "An unexpected error occurred while creating the case";
        showError(`${message}. Please remove invalid files and try again.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/cases");
  };


  const onFileChange = (newFiles: (FileUpload | CaseFile)[]) => {
    console.info("Files changed:", newFiles.length);
    setFileProgress({}); // Reset progress on new file selection
    setUpdatedFiles(newFiles);
    setFiles(newFiles);
  };
  const isValid = useMemo(() => {
    return updatedFiles.length > 0;
  }, [updatedFiles]);

  return (
    <Minimal hidePages={true}>
      <Stack
        maxWidth={550}
        mb={4}
        alignItems={"center"}
        spacing={2}
        textAlign="center"
      >
        <img src={FileImage} />
        <Typography color={"success"} fontWeight={"bold"} variant="h5">
          {Translations.pages.new_case_files_page.header_block.title}
        </Typography>
        <Typography variant="body1" fontWeight="light">
          {Translations.pages.new_case_files_page.header_block.content}
        </Typography>

        <FileUploadZone
          isLoading={isLoading || isSubmitting}
          acceptZip
          onChange={onFileChange}
          buttonPosition="top"
          title={Translations.pages.files_page.upload_block.title}
          description={Translations.pages.files_page.upload_block.content}
          buttonText={Translations.pages.files_page.upload_block.button}
          renderFileList={(files) => (
            <FileListWithProgress
              scrollMaxHeight={"38vh"}
              files={updatedFiles}
              progressMap={fileProgress}
              disabled={isLoading || isSubmitting}
              onChange={setUpdatedFiles}
              onClearProgressKey={(key) =>
                setFileProgress((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                })
              }
            />
          )}
        />
      </Stack>

      <BottomBar
        buttons={[
          <Button
            disabled={isLoading || isSubmitting}
            sx={{ color: "black", fontWeight: "light" }}
            onClick={handleCancel}
            key={1}
            variant="text"
          >
            {Translations.pages.new_case_files_page.buttons.cancel}
          </Button>,
          <Button
            sx={{ px: 4 }}
            onClick={handleAnalyze}
            key={0}
            disabled={!isValid || isLoading || isSubmitting || updatedFiles.length === 0}
            variant="green"
          >
            {isSubmitting ? (
              <CircularProgress sx={{ color: "white" }} size={20} />
            ) : (
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                <East />
                {Translations.pages.files_page.buttons.analyze}
              </Stack>
            )}
          </Button>,
        ]}
      />
    </Minimal>
  );
};

const Create: React.FC = () => {
  if (UNIFIED_UPLOAD_ENABLED) return <CreateV2 />;
  return <CreateV1 />;
};

export default Create;
